'use client';

import { FormEvent, useState } from 'react';

import Skeleton from '../../components/Skeleton';
import TileData from '../../components/TileData';
import { DeletingLabels } from '../lib/labels';

import { pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, Checkbox } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { useRouter } from '@lib/navigation';
import { logger } from '@logger';
import { logout } from '@utils/api/client';
import { useUserStore } from '@utils/user';
import { User } from 'types/user';

interface Props {
  locale: string;
  labels: DeletingLabels;
  user: User | null;
}

const DeleteAccount = ({ locale, labels, user }: Props) => {
  const [confirm, setConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const userStore = useUserStore();

  const router = useRouter();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isDeleting || !confirm) {
      return;
    }

    setIsDeleting(true);
    try {
      const userAnalyticsData = userStore.getUserInfoForAnalytics();
      const response = await fetch('/apis/users/delete', {
        method: 'DELETE',
      });
      if (response.status === 200) {
        pushSimpleGAEvent('transinfo_delete_account', {
          ...userAnalyticsData,
          locale,
          transinfo_view_category: 'delete-from-profile'
        });
        toastSuccess(labels.toast.success);
        router.push('/');
        logout();
        userStore.reset();
        return;
      }
      toastError(labels.toast.error);
    } catch (e) {
      logger.error('Failed to delete user.');
      toastError(labels.toast.error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return <Skeleton title={labels.title} />;
  }

  return (
    <TileData className="lg:col-span-2" title={labels.title}>
      <form className="h-full" onSubmit={onSubmit}>
        <div className="flex h-full flex-col justify-between gap-4">
          <Checkbox
            label={labels.confirm}
            checked={confirm}
            wrapperClassName="!text-sm items-center"
            onChange={() => {
              setConfirm((state) => !state);
            }}
          ></Checkbox>
          <Button disabled={!confirm || isDeleting}>{labels.delete}</Button>
        </div>
      </form>
    </TileData>
  );
};

export default DeleteAccount;
